import addEventListenerWrap from '../../vc-util/Dom/addEventListener';
import raf from '../../_util/raf';
import { defineComponent, onUnmounted, computed, ref, watchEffect, getCurrentInstance, inject, } from 'vue';
import { useInjectTableContext } from '../../table/context';
import supportsPassive from '../../_util/supportsPassive';
import { FullTableRefKey, HasDataKey, ResizeColumnStartRefKey, ResizeColumnMoveRefKey, useInjectInitScroll, ResizeColumnStatusKey, } from '../context/ResizeContext';
const events = {
    mouse: {
        start: 'mousedown',
        move: 'mousemove',
        stop: 'mouseup',
    },
    touch: {
        start: 'touchstart',
        move: 'touchmove',
        stop: 'touchend',
    },
};
const defaultMinWidth = 50;
export default defineComponent({
    compatConfig: { MODE: 3 },
    name: 'DragHandle',
    props: {
        prefixCls: String,
        width: {
            type: Number,
            default: defaultMinWidth,
        },
        minWidth: {
            type: Number,
            default: defaultMinWidth,
        },
        maxWidth: {
            type: Number,
            default: Infinity,
        },
        column: {
            type: Object,
            default: undefined,
        },
        resizeColumn: { type: Function },
    },
    setup(props) {
        let startX = 0;
        let moveEvent = { remove: () => { } };
        let stopEvent = { remove: () => { } };
        const removeEvents = () => {
            moveEvent.remove();
            stopEvent.remove();
        };
        onUnmounted(() => {
            removeEvents();
        });
        watchEffect(() => {
            // if (process.env.NODE_ENV !== 'production') devWarning(!isNaN(props.width), 'Table', 'width must be a number when use resizable');
        });
        const resizeColumnMoveRef = inject(ResizeColumnMoveRefKey);
        const hasData = inject(HasDataKey);
        const fullTableRef = inject(FullTableRefKey);
        const resizeColumnStartRef = inject(ResizeColumnStartRefKey);
        const isResizingHandle = inject(ResizeColumnStatusKey);
        const { onInitTableScroll } = useInjectInitScroll();
        const resizeHandleRef = ref();
        const { onResizeColumn } = useInjectTableContext();
        const minWidth = computed(() => {
            return typeof props.minWidth === 'number' && !isNaN(props.minWidth)
                ? props.minWidth
                : defaultMinWidth;
        });
        const maxWidth = computed(() => {
            return typeof props.maxWidth === 'number' && !isNaN(props.maxWidth)
                ? props.maxWidth
                : Infinity;
        });
        const instance = getCurrentInstance();
        let baseWidth = 0;
        const dragging = ref(false);
        let rafId;
        let rafId2;
        const updateWidth = (e) => {
            let pageX = 0;
            if (e.touches) {
                if (e.touches.length) {
                    // touchmove
                    pageX = e.touches[0].pageX;
                }
                else {
                    // touchend
                    pageX = e.changedTouches[0].pageX;
                }
            }
            else {
                pageX = e.pageX;
            }
            const tmpDeltaX = startX - pageX;
            let w = Math.max(baseWidth - tmpDeltaX, minWidth.value);
            w = Math.min(w, maxWidth.value);
            raf.cancel(rafId);
            props.resizeColumn(w);
            rafId = raf(() => {
                onResizeColumn(w, props.column.__originColumn__);
            });
            raf.cancel(rafId2);
            rafId2 = raf(() => {
                onInitTableScroll && onInitTableScroll();
            });
        };
        const handleMove = e => {
            // updateWidth(e);
            if (!resizeColumnMoveRef.value || !fullTableRef.value) {
                return;
            }
            resizeColumnMoveRef.value.style.display = 'inherit';
            let pageX = 0;
            if (e.touches) {
                pageX = e.touches.length ? e.touches[0].pageX : e.changedTouches[0].pageX;
            }
            else {
                pageX = e.pageX;
            }
            const tmpDeltaX = startX - pageX;
            let w = Math.max(baseWidth - tmpDeltaX, minWidth.value);
            w = Math.min(w, maxWidth.value);
            const fullTableRefLeft = fullTableRef.value.getBoundingClientRect().left;
            const startLeft = resizeHandleRef.value?.parentNode?.getBoundingClientRect().left -
                fullTableRefLeft;
            // 更新拖拽线位置
            if (minWidth.value < w && w < maxWidth.value) {
                resizeColumnMoveRef.value.style.left = `${startLeft + w}px`;
            }
            else if (w >= maxWidth.value) {
                resizeColumnMoveRef.value.style.left = `${startLeft + maxWidth.value - 2}px`;
            }
            else {
                resizeColumnMoveRef.value.style.left = `${startLeft + minWidth.value - 2}px`;
            }
        };
        const handleStop = (e) => {
            dragging.value = false;
            isResizingHandle.value = false;
            resizeColumnMoveRef.value && (resizeColumnMoveRef.value.style.display = 'none');
            resizeColumnStartRef.value && (resizeColumnStartRef.value.style.display = 'none');
            updateWidth(e);
            removeEvents();
        };
        const handleStart = (e, eventsFor) => {
            dragging.value = true;
            isResizingHandle.value = true;
            removeEvents();
            baseWidth = instance.vnode.el.parentNode.getBoundingClientRect().width;
            if (e instanceof MouseEvent && e.which !== 1) {
                return;
            }
            if (e.stopPropagation)
                e.stopPropagation();
            startX = e.touches ? e.touches[0].pageX : e.pageX;
            moveEvent = addEventListenerWrap(document.documentElement, eventsFor.move, handleMove);
            stopEvent = addEventListenerWrap(document.documentElement, eventsFor.stop, handleStop);
        };
        const handleDown = (e) => {
            e.stopPropagation();
            e.preventDefault();
            if (resizeColumnStartRef.value && resizeColumnMoveRef.value) {
                resizeColumnStartRef.value.style.left = `${e.target.getBoundingClientRect().left -
                    fullTableRef.value.getBoundingClientRect().left +
                    e.target.clientWidth / 2 -
                    1}px`;
                const headerEls = fullTableRef.value?.querySelectorAll(`.${props.prefixCls}-header`);
                const tbodyEls = fullTableRef.value?.querySelectorAll(`.${props.prefixCls}-tbody`);
                // 无数据时拖拽的引导线高度仅表头高
                if (!hasData.value && headerEls?.length) {
                    resizeColumnStartRef.value.style.height = `${headerEls[0]?.clientHeight}px`;
                    resizeColumnMoveRef.value.style.height = `${headerEls[0]?.clientHeight}px`;
                }
                else {
                    // 有数据时拖拽的引导线高度仅为真是表格高度，最高为列表可视高度
                    const flag = headerEls?.length &&
                        tbodyEls?.length &&
                        headerEls[0]?.clientHeight + tbodyEls[0]?.clientHeight <
                            fullTableRef.value.scrollHeight;
                    resizeColumnStartRef.value.style.height = flag
                        ? `${headerEls[0]?.clientHeight + tbodyEls[0]?.clientHeight}px`
                        : `${fullTableRef.value?.clientHeight}px`;
                    resizeColumnMoveRef.value.style.height = flag
                        ? `${headerEls[0]?.clientHeight + tbodyEls[0]?.clientHeight}px`
                        : `${fullTableRef.value?.clientHeight}px`;
                }
                resizeColumnStartRef.value.style.display = 'inherit';
            }
            handleStart(e, events.mouse);
        };
        const handleTouchDown = (e) => {
            e.stopPropagation();
            e.preventDefault();
            handleStart(e, events.touch);
        };
        const handleClick = (e) => {
            e.stopPropagation();
            e.preventDefault();
        };
        return () => {
            const { prefixCls } = props;
            const touchEvents = {
                [supportsPassive ? 'onTouchstartPassive' : 'onTouchstart']: e => handleTouchDown(e),
            };
            return (<div class={`${prefixCls}-resize-handle ${dragging.value ? 'dragging' : ''}`} ref={resizeHandleRef} onMousedown={handleDown} {...touchEvents} onClick={handleClick}>
          <div class={`${prefixCls}-resize-handle-line`}></div>
        </div>);
        };
    },
});
